/**
 * Logout page — visited either via the dropdown logout button or by direct
 * URL navigation (`/logout`). Performs a full session purge and redirects.
 *
 * Why a dedicated page (not just a function call):
 *  - The UAT tester hit /logout directly and got a blank screen because no
 *    route matched. This caused the dreaded "stuck on Loading…" state.
 *  - Centralising cleanup here means every entry point (dropdown click,
 *    direct URL, expired session) gets the same hygiene.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ScensobConnectLogo from '../components/brand/ScensobConnectLogo';

export default function LogoutPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Best-effort full purge — runs even if the user already had no token.
    // IMPORTANT: pass {silent: true} so AuthContext.logout() does NOT
    // do its own window.location.assign('/logout') — otherwise we hit an
    // infinite reload loop (LogoutPage → logout() → assign('/logout') →
    // LogoutPage → ...) and `navigate('/login')` below never gets to run.
    try { logout({ silent: true }); } catch (_) { /* AuthContext absent in odd edge cases */ }

    // Clear the server-side httpOnly cookie (Feb 20, 2026 iter76). Fire and
    // forget — do NOT block the redirect on a network hiccup.
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      if (API_URL) {
        fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        }).catch(() => { /* silent */ });
      }
    } catch (_) { /* noop */ }

    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Strip any other auth artefacts (push-notification subs, draft form
      // state, cached profile, etc.) so the next login starts fresh.
      Object.keys(localStorage).forEach((k) => {
        if (/^(auth|jwt|session|profile|cache|push)/i.test(k)) {
          localStorage.removeItem(k);
        }
      });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('localStorage purge failed:', e?.message);
    }

    try {
      sessionStorage.clear();
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.debug('sessionStorage clear failed:', e?.message);
    }

    // Redirect on the next tick so React can finish the auth-state update
    // before the route change (prevents the protected-route flicker).
    const t = setTimeout(() => {
      navigate('/login', { replace: true });
    }, 100);

    return () => clearTimeout(t);
  }, [logout, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50" data-testid="logout-page">
      <ScensobConnectLogo variant="mark" size={64} className="mb-4" />
      <h1 className="text-xl font-semibold text-slate-800">Signing you out&hellip;</h1>
      <p className="text-sm text-slate-500 mt-2">You'll be redirected to the sign-in page shortly.</p>
    </div>
  );
}
